import { useEffect, useId, useState } from "react";

type MermaidApi = {
  initialize: (options: Record<string, unknown>) => void;
  render: (id: string, chart: string) => Promise<{ svg: string }>;
};

declare global {
  interface Window {
    mermaid?: MermaidApi;
  }
}

type MermaidProps = {
  chart: string;
};

let mermaidLoader: Promise<MermaidApi> | undefined;

const loadMermaid = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Mermaid can only render in the browser"));
  }

  if (window.mermaid) {
    return Promise.resolve(window.mermaid);
  }

  mermaidLoader ??= new Promise<MermaidApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js";
    script.async = true;
    script.onload = () => {
      if (!window.mermaid) {
        reject(new Error("Mermaid failed to load"));
        return;
      }

      resolve(window.mermaid);
    };
    script.onerror = () => reject(new Error("Mermaid failed to load"));
    document.head.appendChild(script);
  });

  return mermaidLoader;
};

export const Mermaid = ({ chart }: MermaidProps) => {
  const id = useId();
  const [svg, setSvg] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    void loadMermaid()
      .then(async (mermaid) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "default",
        });

        const safeId = `mermaid-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
        const result = await mermaid.render(safeId, chart);

        if (!cancelled) {
          setSvg(result.svg);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSvg(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chart, id]);

  if (svg) {
    return (
      <div
        aria-label="Workflow diagram"
        dangerouslySetInnerHTML={{ __html: svg }}
        style={{ margin: "1.25rem 0", overflowX: "auto" }}
      />
    );
  }

  return (
    <pre
      aria-label="Workflow diagram source"
      style={{
        border: "1px solid hsl(var(--border))",
        borderRadius: "0.5rem",
        fontSize: "0.875rem",
        margin: "1.25rem 0",
        overflowX: "auto",
        padding: "1rem",
      }}
    >
      {chart}
    </pre>
  );
};
